import { useAppSelector } from "../../core/redux/ReduxStore";
import { forwardRef, useEffect, useState } from "react";
import { ApiSchemaAppointmentDetails } from "../../models/api";
import { Whisper, Popover, Loader } from "rsuite";
import { Image } from "../../components/image.component";
import { AppRouteApi } from "../../config/appRoute";
import { ApiAppointment } from "../../core/apis/appointment";
import { useParams } from "react-router-dom";
import { UtilityDate } from "../../utils/dateUtils";
import { addMinutes } from "date-fns";

type SingleAppointmentProps = {
  appointment: ApiSchemaAppointmentDetails;
};

const SingleAppointment = forwardRef<HTMLDivElement, SingleAppointmentProps>(
  ({ appointment, ...props }, ref) => {
    return (
      <Popover ref={ref} {...props}>
        <div className="appointment-popover">
          {appointment.patientSKey ? (
            <>
              <div className="d-flex">
                <div>
                  <Image
                    src={
                      appointment.patientProfilePic
                        ? AppRouteApi.Media.ByPath(
                            appointment.patientProfilePic
                          )
                        : ""
                    }
                    alt="patient profile"
                    className="rounded-circle object-fit-cover"
                    width={40}
                    height={40}
                  />
                </div>
                <div className="ms-2">
                  <p>
                    {appointment.patientName}
                    <br />
                    {appointment.patientPhoneNumber}
                  </p>
                </div>
              </div>

              <div className="py-2 border-bottom border-1">
                <p>{appointment.patientSpecialInstructions}</p>
              </div>
            </>
          ) : (
            <p className="pb-2 border-bottom border-1">Patient is deleted</p>
          )}

          <div className="py-2 border-bottom border-1">
            <p>
              <b>Start Time: </b>
              {UtilityDate.formatTime(new Date(appointment.taskStartTime))}
              <br />
              <b>End Time: </b>
              {UtilityDate.formatTime(
                addMinutes(
                  new Date(appointment.taskStartTime),
                  appointment.maxHours
                )
              )}
            </p>
          </div>

          <div className="py-2 border-bottom border-1">
            {appointment.patientSKey ? (
              <p>
                <b>Appointed Caregiver: </b>
                {appointment.caregiverName}
                <br />
                {appointment.taskList?.join(", ")}
              </p>
            ) : (
              <p>Caregiver is not selected yet</p>
            )}
          </div>

          <div className="pt-2">
            <p>{appointment.maxHours} hours</p>
          </div>
        </div>
      </Popover>
    );
  }
);


export const DashboardSidebar = () => {
  const { selectedDate } = useAppSelector((state) => state.appointmentCalendar);
  const [appointments, setAppointments] = useState<
    ApiSchemaAppointmentDetails[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const { companyId: companyIdParam } = useParams<{ companyId: string }>();

  const weekdays = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  useEffect(() => {
    setIsLoading(true);
    ApiAppointment.ByDate(selectedDate, companyIdParam).then((appointments) => {
      setAppointments(appointments || []);
      setIsLoading(false);
    });
  }, [companyIdParam, selectedDate]);

  return (
    <div className="row ">
      <div className="col-12 mb-4">
        <div className="text-dark fs-5">
          {weekdays[new Date(selectedDate).getDay()]}{" "}
          {new Date(selectedDate).getDate()}
        </div>
      </div>
      {isLoading ? (
        <Loader />
      ) : appointments?.length ? (
        appointments?.map((item) => (
          <Whisper
            key={item.sKey}
            placement="left"
            speaker={<SingleAppointment appointment={item} />}
          >
            <div className="col-12 schedule-detail-info mb-4">
              <div className="row">
                <div className="col-md-5 col-12">
                  <p className="text-muted-green">
                    {UtilityDate.formatTime(new Date(item.taskStartTime))} -{" "}
                    {UtilityDate.formatTime(
                      addMinutes(
                        new Date(item.taskStartTime),
                        item.maxHours * 60
                      )
                    )}
                  </p>
                </div>
                <div className="col-md-7 col-12 line-container">
                  <hr />
                </div>
              </div>
              {item.patientSKey ? (
                <div className="row my-1">
                  <div className="col-md-3 col-12">Patient:</div>
                  <div className="col-md-9 col-12">{item.patientName}</div>
                </div>
              ) : (
                <p className="text-muted">Patient is deleted</p>
              )}
              {item?.caregiverSKey ? (
                <div className="row my-1">
                  <div className="col-md-3 col-12">Caregiver:</div>
                  <div className="col-md-9 col-12">{item.caregiverName}</div>
                </div>
              ) : (
                <p className="text-muted mb-3">Caregiver is not selected yet</p>
              )}
            </div>
          </Whisper>
        ))
      ) : (
        <div className="col-12">No appointments available.</div>
      )}
    </div>
  );
};
